(function () {
  const rootSelector = '[data-bundle-builder]';
  const sheetOpenClass = 'dt-bundle-sheet-open';

  function moneyText(cents, fallback) {
    if (fallback) return fallback;
    const value = Number(cents || 0) / 100;
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value);
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>\"']/g, (character) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;'
    }[character]));
  }

  function trackNativeBundleEvent(eventName, parameters) {
    if (typeof window.dtTrackNativeBundleEvent === 'function') {
      window.dtTrackNativeBundleEvent(eventName, parameters);
      return;
    }
    try {
      if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        window.dataLayer.push(Object.assign({ event: eventName }, parameters || {}));
      }
      if (typeof window.gtag === 'function') window.gtag('event', eventName, parameters || {});
    } catch (_) {}
  }

  function parseProduct(card) {
    const node = card.querySelector('[data-product-json]');
    if (!node) return null;
    try {
      const product = JSON.parse(node.textContent);
      product.group = card.dataset.groupKey;
      return product;
    } catch (error) {
      console.warn('[Latest Offers] Product JSON failed', error);
      return null;
    }
  }

  function createNativeBundleInstanceId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return `dtb_${window.crypto.randomUUID()}`;
    }
    const randomValues = new Uint32Array(2);
    if (window.crypto && typeof window.crypto.getRandomValues === 'function') {
      window.crypto.getRandomValues(randomValues);
    } else {
      randomValues[0] = Math.floor(Math.random() * 0x100000000);
      randomValues[1] = Math.floor(Math.random() * 0x100000000);
    }
    return `dtb_${Date.now().toString(36)}_${randomValues[0].toString(36)}${randomValues[1].toString(36)}`;
  }

  class BundleBuilder {
    constructor(root) {
      this.root = root;
      this.sheet = root.querySelector('[data-bundle-sheet]');
      this.sheetBody = root.querySelector('[data-sheet-body]');
      this.sheetPanel = root.querySelector('.dt-bundle-sheet__panel');
      this.error = root.querySelector('[data-bundle-error]');
      this.selectedList = root.querySelector('[data-selected-list]');
      this.addBundleButton = root.querySelector('[data-add-bundle]');
      this.lastFocus = null;
      this.currentProduct = null;
      this.currentGroup = null;
      this.currentOptions = [];
      this.pdpInfoIcons = {
        Specifications: root.dataset.specificationsIcon,
        'Product Details': root.dataset.productDetailsIcon,
        'Usage & Care': root.dataset.usageCareIcon,
        Disclaimer: root.dataset.disclaimerIcon
      };
      this.chevronIcon = root.dataset.chevronIcon;
      this.bundleExpanded = false;
      this.sheetCloseTimer = null;
      this.sheetCloseHandler = null;
      this.sheetOpenFrame = null;
      this.sheetOpenNestedFrame = null;
      this.sheetStateVersion = 0;
      this.groups = Array.from(root.querySelectorAll('[data-bundle-group]')).map((group) => ({
        key: group.dataset.groupKey,
        required: Number(group.dataset.groupRequired || 0),
        element: group
      }));
      this.totalRequired = Number(root.dataset.totalRequired || 0);
      this.storageKey = `dt-bundle-offers:${root.dataset.bundleId || 'bundle'}`;
      this.selectedItems = this.restore();
      this.sanitizeSelectedItems();
      this.bind();
      this.render();
      trackNativeBundleEvent('dt_native_bundle_viewed', {
        bundle_source: 'native',
        bundle_id: this.root.dataset.bundleId || 'bundle_offer',
        bundle_price: Number(this.root.dataset.priceCents || 0) / 100,
        main_required: Number(this.root.dataset.mainRequired || 0),
        addon_required: Number(this.root.dataset.addonRequired || 0),
        page_path: window.location.pathname
      });
    }

    selectedItemForProduct(product) {
      if (!product) return null;
      return this.selectedItems.find((item) => {
        if (item.group !== product.group) return false;
        if (item.familyProductId === product.id || item.productId === product.id) return true;
        return Array.isArray(product.sourceProductIds) && product.sourceProductIds.includes(item.productId);
      }) || null;
    }

    bind() {
      this.root.addEventListener('click', (event) => {
        const closeButton = event.target.closest('[data-sheet-close]');
        if (closeButton) {
          this.closeSheet();
          return;
        }

        if (event.target.closest('[data-bundle-conflict-keep]')) {
          this.closeSheet();
          return;
        }

        if (event.target.closest('[data-bundle-conflict-replace]')) {
          this.replaceExistingBundle();
          return;
        }

        const removeButton = event.target.closest('[data-remove-selected]');
        if (removeButton) {
          this.removeSelected(removeButton.dataset.removeSelected);
          return;
        }

        const toggleButton = event.target.closest('[data-bundle-toggle]');
        if (toggleButton) {
          this.bundleExpanded = !this.bundleExpanded;
          this.render();
          return;
        }

        const addButton = event.target.closest('[data-add-product]');
        if (addButton) {
          const card = addButton.closest('[data-bundle-card]');
          if (card.classList.contains('is-selected')) {
            const product = parseProduct(card);
            const selectedItem = this.selectedItemForProduct(product);
            if (selectedItem) this.removeSelected(selectedItem.uid);
            return;
          }
          this.handleProductAdd(card, addButton);
          return;
        }

        const previewButton = event.target.closest('[data-open-quick-view]');
        if (previewButton) {
          const card = previewButton.closest('[data-bundle-card]');
          this.openQuickView(card, previewButton);
          return;
        }

        const optionButton = event.target.closest('[data-option-value]');
        if (optionButton) {
          this.chooseOption(optionButton);
          return;
        }

        const quickVariantButton = event.target.closest('[data-quick-variant]');
        if (quickVariantButton) {
          this.chooseQuickVariant(quickVariantButton);
          return;
        }

        const infoToggle = event.target.closest('[data-product-info-toggle]');
        if (infoToggle) {
          const info = infoToggle.closest('.dt-bundle-sheet-product__info');
          const expanded = !info.classList.contains('is-open');
          info.classList.toggle('is-open', expanded);
          infoToggle.setAttribute('aria-expanded', String(expanded));
          return;
        }

        const sheetAdd = event.target.closest('[data-sheet-add]');
        if (sheetAdd) {
          this.addCurrentVariant();
          return;
        }

        if (event.target.closest('[data-add-bundle]')) {
          this.addBundleToCart();
        }
      });

      document.addEventListener('keydown', (event) => {
        if (!this.sheet || this.sheet.hidden) return;
        if (event.key === 'Escape') this.closeSheet();
        if (event.key === 'Tab') this.trapFocus(event);
      });
    }

    restore() {
      try {
        const raw = sessionStorage.getItem(this.storageKey);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed : [];
      } catch (_) {
        return [];
      }
    }

    persist() {
      try {
        sessionStorage.setItem(this.storageKey, JSON.stringify(this.selectedItems));
      } catch (_) {}
    }

    resolveDisplayPrice(product, variant) {
      const candidates = [variant, product].concat(product?.variants || []);
      for (const candidate of candidates) {
        const amount = Number(candidate?.compareAtPrice || candidate?.mrpPrice || candidate?.price || 0);
        if (amount <= 0) continue;
        const storedText = candidate?.mrpPriceText || candidate?.priceText || '';
        const storedTextAmount = Number(String(storedText).replace(/[^0-9]/g, ''));
        return {
          amount,
          text: storedTextAmount > 0 ? storedText : moneyText(amount, '')
        };
      }
      return { amount: 0, text: '' };
    }

    sanitizeSelectedItems() {
      const productsByGroup = new Map();
      this.groups.forEach((group) => {
        const products = new Map();
        group.element.querySelectorAll('[data-bundle-card]').forEach((card) => {
          const product = parseProduct(card);
          if (product) products.set(String(product.id), product);
        });
        productsByGroup.set(group.key, products);
      });

      const groupCounts = new Map();
      const variantIds = new Set();
      this.selectedItems = this.selectedItems.filter((item) => {
        const group = this.groups.find((entry) => entry.key === item.group);
        const product = productsByGroup.get(item.group)?.get(String(item.productId));
        const variant = product?.variants?.find((entry) => String(entry.id) === String(item.variantId));
        const groupCount = groupCounts.get(item.group) || 0;
        const variantId = String(item.variantId || '');
        if (!group || !product || !variant?.available || !variantId) return false;
        if (groupCount >= group.required || variantIds.has(variantId)) return false;
        if (Number(item.mrpPrice || 0) <= 0) {
          const price = this.resolveDisplayPrice(product, variant);
          item.mrpPrice = price.amount;
          item.priceText = price.text;
        }
        groupCounts.set(item.group, groupCount + 1);
        variantIds.add(variantId);
        return true;
      });
      this.persist();
    }

    countGroup(groupKey) {
      return this.selectedItems.filter((item) => item.group === groupKey).length;
    }

    selectedTotal() {
      return this.selectedItems.length;
    }

    remainingTotal() {
      return Math.max(this.totalRequired - this.selectedTotal(), 0);
    }

    isComplete() {
      return this.groups.every((group) => this.countGroup(group.key) === group.required);
    }

    groupHasRoom(groupKey) {
      const group = this.groups.find((entry) => entry.key === groupKey);
      if (!group) return false;
      return this.countGroup(group.key) < group.required;
    }

    hasVariant(variantId) {
      return this.selectedItems.some((item) => String(item.variantId) === String(variantId));
    }

    handleProductAdd(card, trigger) {
      const product = parseProduct(card);
      if (!product || !product.available) return;
      if (!this.groupHasRoom(product.group)) {
        this.showError('Limit reached for this section. Remove one item to replace it.');
        return;
      }
      this.lastFocus = trigger || document.activeElement;
      if ((product.hasOnlyDefaultVariant || !product.hasVariantChoices) && product.variants && product.variants[0] && product.variants[0].available) {
        this.addItem(product, product.variants[0]);
        return;
      }
      this.openVariantSheet(product, product.group);
    }

    openQuickView(card, trigger) {
      const product = parseProduct(card);
      if (!product) return;
      this.lastFocus = trigger || document.activeElement;
      this.currentProduct = product;
      this.currentGroup = product.group;
      this.currentOptions = [];
      this.sheet.classList.add('is-product-details');
      const images = product.images && product.images.length ? product.images : [product.featuredImage].filter(Boolean);
      const gallery = images.map((src, index) => `<img src="${src}" alt="${this.escape(product.title)}" loading="${index === 0 ? 'eager' : 'lazy'}"${index === 0 ? ' fetchpriority="high"' : ''}>`).join('');
      const firstOption = product.options[0];
      const initialVariant = product.variants.find((variant) => variant.available) || product.variants[0];
      this.currentOptions = initialVariant?.options ? initialVariant.options.slice() : [];
      const facts = [
        {
          title: 'Specifications',
          content: `<h3>Description</h3><h4>${this.escape(product.title)}</h4>${product.descriptionHtml || `<p>${this.escape(product.description || '')}</p>`}<h3>Specification &amp; Care</h3><p>${this.escape(product.specs || '')}</p>`
        },
        {
          title: 'Product Details',
          content: `<dl><div><dt>MRP(Inclusive of all taxes)</dt><dd>${this.escape(initialVariant?.priceText || product.priceText)}</dd></div><div><dt>Manufacturer's Name</dt><dd>Doctor Safe Towels Factory Pvt. Ltd</dd></div><div><dt>Manufacturer's Address</dt><dd>2B, IV Street, Ramakrishnapuram, Karur - 639001, Tamil Nadu, India.</dd></div><div><dt>Country of Origin</dt><dd>India</dd></div><div><dt>Phone</dt><dd>+91 80698 35407</dd></div><div><dt>Email</dt><dd>customercare@doctortowels.com</dd></div></dl>`
        },
        { title: 'Usage & Care', content: `<p>${this.escape(product.usageCare || product.specs || '')}</p>` },
        { title: 'Disclaimer', content: '<p>Designed to help reduce bacterial growth on its surface, this towel supports everyday hygiene and helps protect sensitive skin. Regular washing and proper care will help it perform at its best. Results may vary with use and individual skin sensitivity.</p>' }
      ];
      const variantCards = firstOption ? firstOption.values.map((value) => {
        const optionVariants = product.variants.filter((item) => item.options[0] === value);
        const variant = optionVariants.find((item) => item.available) || optionVariants[0];
        const soldOut = optionVariants.length > 0 && optionVariants.every((item) => !item.available);
        const selected = variant?.id === initialVariant?.id;
        return `<button class="dt-bundle-sheet-product__variant-card${selected ? ' is-selected' : ''}${soldOut ? ' is-sold-out' : ''}" type="button" data-quick-variant="${this.escape(variant?.id || '')}" aria-pressed="${selected ? 'true' : 'false'}"${soldOut ? ' disabled' : ''}><img src="${this.escape(variant?.image || product.featuredImage || '')}" alt="${this.escape(value)}" loading="lazy">${selected ? '<span>Selected</span>' : (soldOut ? '<span>Sold out</span>' : '')}</button>`;
      }).join('') : '';

      this.sheetBody.innerHTML = `
        <div class="dt-bundle-sheet-product dt-bundle-sheet-product--details">
          <div class="dt-bundle-sheet-product__gallery">${gallery}</div>
          <div class="dt-bundle-sheet-product__details-heading">
            <h2 id="DtBundleSheetTitle">${this.escape(product.title)}</h2>
            <span class="dt-bundle-sheet-product__rating" data-quick-rating aria-label="${this.escape(product.rating || 4.9)} out of 5 customer rating"><span aria-hidden="true">★★★★★</span> <small>${this.escape(product.rating || 4.9)}</small></span>
          </div>
          <p class="dt-bundle-sheet-product__price">${this.escape(initialVariant?.priceText || product.priceText)}</p>
          <div class="dt-bundle-sheet-product__details-copy">
            ${variantCards ? `<p class="dt-bundle-sheet-product__variant-title">Choose Variant: <span>${this.escape(initialVariant?.options?.[0] || '')}</span></p><div class="dt-bundle-sheet-product__variant-cards">${variantCards}</div>` : ''}
            ${facts.map((row) => `<section class="dt-bundle-sheet-product__info"><button type="button" data-product-info-toggle aria-expanded="false"><span>${this.pdpInfoIcons[row.title] ? `<img src="${this.pdpInfoIcons[row.title]}" width="20" height="20" alt="" aria-hidden="true">` : ''}${this.escape(row.title)}</span>${this.chevronIcon ? `<img src="${this.chevronIcon}" width="16" height="16" alt="" aria-hidden="true">` : ''}</button><div class="dt-bundle-sheet-product__detail-content"><div class="dt-bundle-sheet-product__detail-rich">${row.content}</div></div></section>`).join('')}
          </div>
          <button class="dt-bundle-sheet-product__cta" type="button" data-sheet-add ${initialVariant?.available ? '' : 'disabled'}>${initialVariant?.available ? 'Add to Bundle' : 'Sold out'}</button>
        </div>
      `;
      this.openSheet();
    }

    chooseQuickVariant(button) {
      const variant = this.currentProduct?.variants.find((item) => String(item.id) === button.dataset.quickVariant);
      if (!variant || !variant.available) return;
      this.currentOptions = variant.options.slice();
      this.sheetBody.querySelectorAll('[data-quick-variant]').forEach((item) => {
        const selected = item === button;
        item.classList.toggle('is-selected', selected);
        item.setAttribute('aria-pressed', String(selected));
        const label = item.querySelector('span');
        if (label) label.remove();
      });
      button.insertAdjacentHTML('beforeend', '<span>Selected</span>');
      const variantTitle = this.sheetBody.querySelector('.dt-bundle-sheet-product__variant-title span');
      if (variantTitle) variantTitle.textContent = variant.options[0] || '';
      const price = this.sheetBody.querySelector('.dt-bundle-sheet-product--details .dt-bundle-sheet-product__price');
      if (price) price.textContent = variant.priceText || this.currentProduct.priceText;
      const gallery = this.sheetBody.querySelector('.dt-bundle-sheet-product__gallery');
      const galleryImages = gallery ? Array.from(gallery.querySelectorAll('img')) : [];
      const variantImagePath = this.imagePath(variant.image);
      const imageIndex = galleryImages.findIndex((image) => this.imagePath(image.currentSrc || image.src) === variantImagePath);
      if (gallery && imageIndex > -1) gallery.scrollTo({ left: galleryImages[imageIndex].offsetLeft, behavior: 'smooth' });
      this.updateVariantCta();
    }

    imagePath(source) {
      if (!source) return '';
      try {
        return new URL(source, window.location.origin).pathname;
      } catch (error) {
        return String(source).split('?')[0];
      }
    }

    thumbnailUrl(source) {
      if (!source) return '';
      try {
        const url = new URL(source, window.location.origin);
        if (!/cdn\.shopify\.com$/i.test(url.hostname)) return source;
        url.searchParams.set('width', '96');
        return url.toString();
      } catch (_) {
        return source;
      }
    }

    openVariantSheet(product, groupKey) {
      this.currentProduct = product;
      this.currentGroup = groupKey;
      this.currentOptions = product.options.map(() => null);
      this.sheet.classList.remove('is-product-details');
      const singleOption = product.options.length === 1;
      const initialVariant = product.variants.find((variant) => variant.available) || product.variants[0];
      const initialLabel = initialVariant?.options?.[0] || '';
      const thumb = product.featuredImage ? `<img class="dt-bundle-sheet-product__thumb" src="${product.featuredImage}" alt="${this.escape(product.title)}" loading="lazy">` : '';
      const optionGroups = product.options.map((option, optionIndex) => `
        <fieldset class="dt-bundle-option-group">
          <legend>${this.escape(option.name)}</legend>
          <div class="dt-bundle-option-group__values">
            ${option.values.map((value) => {
              const optionVariant = product.variants.find((variant) => variant.options[optionIndex] === value);
              const optionImage = optionVariant?.image || product.featuredImage || '';
              const soldOut = !product.variants.some((variant) => variant.options[optionIndex] === value && variant.available);
              return `<button class="dt-bundle-option${soldOut ? ' is-sold-out' : ''}" type="button" data-option-index="${optionIndex}" data-option-value="${this.escape(value)}" data-option-label="${this.escape(value)}" style="--dt-bundle-option-name-length: ${String(value).length}" aria-pressed="false"${soldOut ? ' disabled aria-disabled="true"' : ''}>${optionImage ? `<img src="${this.escape(optionImage)}" alt="" width="88" height="58" loading="lazy">` : ''}<span>${soldOut ? 'Sold out' : this.escape(value)}</span></button>`;
            }).join('')}
          </div>
        </fieldset>
      `).join('');

      this.sheetBody.innerHTML = `
        <div class="dt-bundle-sheet-product">
          <div class="dt-bundle-sheet-product__top">
            ${thumb}
            <div>
              <h2 id="DtBundleSheetTitle"><span class="dt-bundle-sheet-product__eyebrow">Choose Variant:</span>${initialLabel ? ` <span data-variant-label>${this.escape(initialLabel)}</span>` : ''}</h2>
              <p class="dt-bundle-sheet-product__price">${this.escape(product.priceText)}</p>
              <p class="dt-bundle-sheet-product__specs">${this.escape(product.title)}${product.specs ? ' · ' + this.escape(product.specs) : ''}</p>
            </div>
          </div>
          ${optionGroups}
          ${singleOption ? '' : '<button class="dt-bundle-sheet-product__cta" type="button" data-sheet-add disabled>Add to Bundle</button><button class="dt-bundle-sheet-product__cancel" type="button" data-sheet-close>Cancel</button>'}
        </div>
      `;
      this.updateOptionAvailability();
      this.openSheet();
    }

    chooseOption(button) {
      const index = Number(button.dataset.optionIndex);
      this.currentOptions[index] = button.dataset.optionValue;
      const group = button.closest('.dt-bundle-option-group');
      group.querySelectorAll('[data-option-value]').forEach((item) => item.setAttribute('aria-pressed', 'false'));
      button.setAttribute('aria-pressed', 'true');
      const selectedLabel = this.sheetBody.querySelector('[data-variant-label]');
      if (selectedLabel) selectedLabel.textContent = button.dataset.optionValue || '';
      if (this.currentProduct.options.length === 1) {
        const variant = this.findCurrentVariant();
        if (variant?.available) this.addItem(this.currentProduct, variant);
        return;
      }
      this.updateOptionAvailability();
      this.updateVariantCta();
    }

    updateOptionAvailability() {
      if (!this.currentProduct) return;
      this.sheetBody.querySelectorAll('.dt-bundle-option-group').forEach((group, optionIndex) => {
        const allowedValues = new Set(this.currentProduct.variants
          .filter((variant) => variant.available && variant.options.every((value, index) => index === optionIndex || !this.currentOptions[index] || value === this.currentOptions[index]))
          .map((variant) => variant.options[optionIndex]));
        group.querySelectorAll('[data-option-value]').forEach((button) => {
          const available = allowedValues.has(button.dataset.optionValue);
          const label = button.querySelector('span');
          button.disabled = !available;
          button.classList.toggle('is-sold-out', !available);
          button.setAttribute('aria-disabled', String(!available));
          if (label) label.textContent = available ? button.dataset.optionLabel : 'Sold out';
        });
      });
    }

    findCurrentVariant() {
      if (!this.currentProduct) return null;
      if (this.currentProduct.hasOnlyDefaultVariant || !this.currentProduct.hasVariantChoices) return this.currentProduct.variants[0];
      if (this.currentOptions.some((value) => !value)) return null;
      return this.currentProduct.variants.find((variant) => {
        return variant.options.every((value, index) => value === this.currentOptions[index]);
      });
    }

    updateVariantCta() {
      const cta = this.sheetBody.querySelector('[data-sheet-add]');
      if (!cta) return;
      const variant = this.findCurrentVariant();
      const isSelected = variant && this.hasVariant(variant.id);
      cta.disabled = !variant || !variant.available || isSelected;
      cta.textContent = isSelected ? 'Already selected' : variant && !variant.available ? 'Sold out' : 'Add to Bundle';
    }

    addCurrentVariant() {
      if (!this.currentProduct) return;
      if (this.currentProduct.hasOnlyDefaultVariant) {
        if (this.currentProduct.variants[0] && this.currentProduct.variants[0].available) {
          this.addItem(this.currentProduct, this.currentProduct.variants[0]);
        }
        return;
      }
      if (!this.currentOptions.length || this.currentOptions.some((value) => !value)) {
        this.openVariantSheet(this.currentProduct, this.currentGroup);
        return;
      }
      const variant = this.findCurrentVariant();
      if (!variant || !variant.available) return;
      this.addItem(this.currentProduct, variant);
    }

    addItem(product, variant) {
      if (this.hasVariant(variant.id)) {
        this.showError('This exact option is already in your bundle. Choose a different variant.');
        return;
      }
      if (!this.groupHasRoom(product.group)) {
        this.showError('Limit reached for this section. Remove one item to replace it.');
        return;
      }
      const price = this.resolveDisplayPrice(product, variant);
      const item = {
        uid: `${product.group}-${variant.id}-${Date.now()}`,
        group: product.group,
        productId: variant.productId || product.id,
        familyProductId: product.id,
        variantId: variant.id,
        title: product.title,
        variantTitle: variant.title === 'Default Title' ? '' : variant.title,
        image: variant.image || product.featuredImage || '',
        specs: product.specs || product.type || '',
        priceText: price.text,
        mrpPrice: price.amount,
        quantity: 1
      };
      this.selectedItems.push(item);
      this.persist();
      trackNativeBundleEvent('dt_native_bundle_product_selected', {
        bundle_source: 'native',
        bundle_id: this.root.dataset.bundleId || 'bundle_offer',
        section: product.group === 'addons' ? 'addons' : 'main',
        product_id: String(product.id || ''),
        variant_id: String(variant.id || ''),
        product_name: product.title || '',
        product_price: Number(price.amount || 0) / 100,
        selected_count: this.selectedItems.length
      });
      this.closeSheet();
      this.render();
      this.showError('');
    }

    removeSelected(uid) {
      const removedItem = this.selectedItems.find((item) => item.uid === uid);
      this.selectedItems = this.selectedItems.filter((item) => item.uid !== uid);
      this.persist();
      if (removedItem) {
        trackNativeBundleEvent('dt_native_bundle_product_removed', {
          bundle_source: 'native',
          bundle_id: this.root.dataset.bundleId || 'bundle_offer',
          section: removedItem.group === 'addons' ? 'addons' : 'main',
          product_id: String(removedItem.productId || ''),
          variant_id: String(removedItem.variantId || ''),
          selected_count: this.selectedItems.length
        });
      }
      this.render();
    }

    render() {
      const selectedCount = this.selectedTotal();
      const remaining = this.remainingTotal();
      const complete = this.isComplete();
      this.root.querySelectorAll('[data-selected-count], [data-sticky-count]').forEach((node) => {
        node.textContent = selectedCount;
      });
      const bundlePrice = this.root.dataset.priceText || moneyText(this.root.dataset.priceCents, '');
      const bundlePriceCents = Number(this.root.dataset.priceCents || 0);
      const progressText = complete
        ? `Your bundle is ready at ${bundlePrice}`
        : `Add ${remaining} more product${remaining === 1 ? '' : 's'} to get the bundle at ${bundlePrice}`;
      this.root.querySelectorAll('[data-progress-text], [data-sticky-text]').forEach((node) => {
        node.textContent = progressText;
      });
      this.addBundleButton.disabled = !complete;
      this.addBundleButton.textContent = 'Add Bundle To Cart';
      const savings = this.selectedItems.reduce((total, item) => {
        const storedPrice = Number(item.mrpPrice || 0);
        if (storedPrice) return total + storedPrice;
        const textPrice = Number(String(item.priceText || '').replace(/[^0-9]/g, ''));
        return total + (textPrice ? textPrice * 100 : 0);
      }, 0) - bundlePriceCents;
      const savingsNode = this.root.querySelector('[data-bundle-savings]');
      if (savingsNode) {
        const showSavings = complete && savings > 0;
        savingsNode.hidden = !showSavings;
        const savingsValue = savingsNode.querySelector('[data-bundle-savings-value]');
        if (savingsValue) savingsValue.textContent = String(Math.max(0, Math.round(savings / 100)));
      }
      this.root.classList.toggle('is-bundle-expanded', this.bundleExpanded && selectedCount > 0);
      const toggle = this.root.querySelector('[data-bundle-toggle]');
      if (toggle) {
        const canExpand = selectedCount > 0;
        toggle.disabled = !canExpand;
        toggle.setAttribute('aria-expanded', String(canExpand && this.bundleExpanded));
        toggle.setAttribute('aria-label', this.bundleExpanded ? 'Hide selected bundle products' : 'Show selected bundle products');
      }
      const thumbnailUrls = this.selectedItems.slice(0, 3).map((item) => this.thumbnailUrl(item.image));
      const slots = this.root.querySelector('[data-bundle-slots]');
      if (slots) {
        slots.innerHTML = Array.from({ length: 3 }, (_, index) => {
          const item = this.selectedItems[index];
          const image = thumbnailUrls[index];
          return `<span class="dt-bundle-sticky__slot${image ? ' is-filled' : ''}" aria-hidden="true">${image ? `<img src="${this.escape(image)}" width="32" height="32" alt="">` : '<span>+</span>'}</span>`;
        }).join('');
      }
      this.selectedList.innerHTML = this.selectedItems.map((item) => `
        <div class="dt-bundle-sticky__chip">
          ${item.image ? `<img src="${this.escape(this.thumbnailUrl(item.image))}" alt="" width="32" height="32" loading="lazy">` : ''}
          <span class="dt-bundle-sticky__chip-copy"><span>${this.escape(item.title)}</span>${item.variantTitle ? `<small>${this.escape(item.variantTitle)}</small>` : ''}</span>
          <span class="dt-bundle-sticky__chip-price">${this.escape(item.priceText)}</span>
          <button type="button" data-remove-selected="${this.escape(item.uid)}" aria-label="Remove ${this.escape(item.title)}">&times;</button>
        </div>
      `).join('');
      this.renderCardStates();
    }

    renderCardStates() {
      this.groups.forEach((group) => {
        const filled = this.countGroup(group.key) >= group.required;
        group.element.querySelectorAll('[data-bundle-card]').forEach((card) => {
          const product = parseProduct(card);
          const selected = Boolean(this.selectedItemForProduct(product));
          const unavailable = filled && !selected;
          const soldOut = card.querySelector('[data-add-product]')?.getAttribute('aria-label')?.includes('sold out');
          card.classList.toggle('is-selected', Boolean(selected));
          card.classList.toggle('is-unavailable', Boolean(unavailable));
          card.classList.toggle('is-sold-out', Boolean(soldOut));
          const preview = card.querySelector('[data-open-quick-view]');
          if (preview) {
            preview.disabled = Boolean(unavailable || soldOut);
            preview.setAttribute('aria-disabled', String(Boolean(unavailable || soldOut)));
          }
          const add = card.querySelector('[data-add-product]');
          if (add) {
            const label = add.querySelector('span');
            const icon = add.querySelector('.dt-bundle-product__add-icon');
            add.classList.toggle('is-selected', Boolean(selected));
            add.disabled = !selected && (filled || soldOut);
            if (label) label.textContent = selected ? 'Remove' : (soldOut ? 'Sold out' : 'Add');
            if (icon) icon.hidden = Boolean(selected) || !product.hasVariantChoices;
            add.setAttribute('aria-label', selected ? `Remove ${product.title} from bundle` : (soldOut ? `${product.title} is sold out` : `Add ${product.title} to bundle`));
          }
        });
      });
    }

    async addBundleToCart({ skipExistingBundleCheck = false, replacedBundleItems = [] } = {}) {
      if (!this.isComplete()) return;

      this.addBundleButton.disabled = true;
      this.addBundleButton.classList.add('is-loading');
      this.addBundleButton.setAttribute('aria-busy', 'true');
      this.addBundleButton.textContent = 'Preparing your bundle';
      const cartRuntimePromise = window.dtLoadCartRuntime
        ? window.dtLoadCartRuntime({ reason: 'bundle-offers' })
        : Promise.resolve();
      if (!skipExistingBundleCheck) {
        try {
          const cartResponse = await fetch('/cart.js', { credentials: 'same-origin' });
          const cart = cartResponse.ok ? await cartResponse.json() : null;
          const existingBundleItems = Array.isArray(cart?.items) ? cart.items.filter((item) => (
            item?.properties?._dt_native_bundle_contract === 'dt_native_v1'
          )) : [];
          if (existingBundleItems.length) {
            this.openBundleConflictSheet(existingBundleItems);
            return;
          }
        } catch (_) {
          this.showError("We couldn't check your cart. Please try again before adding this bundle.");
          return;
        }
      }
      const bundleId = this.root.dataset.bundleId || 'bundle_offer';
      const bundleInstanceId = createNativeBundleInstanceId();
      const bundleKey = `${bundleId}:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`;
      const bundleTitle = this.root.dataset.offerTitle || this.root.dataset.displayTitle || 'Bundle Offer';
      const bundleSize = String(this.selectedItems.length);
      const bundlePrice = this.root.dataset.priceText || moneyText(this.root.dataset.priceCents, '');
      const gokwikCoupon = String(this.root.dataset.gokwikDiscountCode || '').trim();
      const mainRequired = String(this.root.dataset.mainRequired || '0');
      const addonRequired = String(this.root.dataset.addonRequired || '0');
      const items = this.selectedItems.map((item, index) => ({
        id: Number(item.variantId),
        quantity: 1,
        properties: {
          _bundle_type: 'bundle_offer',
          _dt_native_bundle_contract: 'dt_native_v1',
          _dt_native_bundle_instance: bundleInstanceId,
          _bundle_id: bundleId,
          _bundle_key: bundleKey,
          _bundle_title: bundleTitle,
          _bundle_size: bundleSize,
          _bundle_price: bundlePrice,
          _bundle_role: item.group === 'addons' ? 'addon' : 'main',
          _bundle_index: String(index + 1),
          _dt_gokwik_checkout_code: gokwikCoupon,
          _dt_bundle_main_required: mainRequired,
          _dt_bundle_addon_required: addonRequired
        }
      }));
      const bundlePriceCents = Number(this.root.dataset.priceCents || 0);

      try {
        await cartRuntimePromise;
        const addRequest = fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items })
        });
        const response = await addRequest;
        if (!response.ok) {
          const payload = await response.json().catch(() => null);
          throw new Error(payload?.description || payload?.message || 'cart_add_failed');
        }
        trackNativeBundleEvent('dt_native_bundle_added_to_cart', {
          bundle_source: 'native',
          bundle_id: bundleId,
          bundle_price: bundlePriceCents / 100,
          item_count: items.length,
          product_ids: items.map((item) => String(item.id)).join(','),
          page_path: window.location.pathname
        });
        sessionStorage.removeItem(this.storageKey);
        this.selectedItems = [];
        this.render();
        if (window.CartController?.finishAddToCartRefresh) {
          await window.CartController.finishAddToCartRefresh({
            refreshRecommendations: true,
            refreshItems: true,
            refreshCartPage: false
          });
        } else {
          window.location.assign(window.routes?.cart_url || '/cart');
        }
      } catch (error) {
        trackNativeBundleEvent('dt_native_bundle_add_failed', {
          bundle_source: 'native',
          bundle_id: bundleId,
          bundle_price: bundlePriceCents / 100,
          error_type: /sold out|not enough|available/i.test(error?.message || '') ? 'availability' : 'request',
          page_path: window.location.pathname
        });
        if (replacedBundleItems.length) {
          try {
            await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ items: replacedBundleItems.map((item) => ({
                id: Number(item.variant_id || item.id),
                quantity: Number(item.quantity || 1),
                properties: item.properties || {}
              })) })
            });
          } catch (_) {}
        }
        const message = /sold out|not enough|available/i.test(error?.message || '')
          ? 'One of these options just sold out. Refresh the page and choose another option.'
          : "We couldn't add this bundle to your cart. Please try again.";
        this.showError(message);
      } finally {
        this.addBundleButton.classList.remove('is-loading');
        this.addBundleButton.removeAttribute('aria-busy');
        this.addBundleButton.textContent = 'Add Bundle To Cart';
        this.addBundleButton.disabled = !this.isComplete();
      }
    }

    openBundleConflictSheet(existingBundleItems) {
      if (!this.sheet || !this.sheetBody || !existingBundleItems.length) return;
      const existingBundle = existingBundleItems[0] || {};
      const existingProperties = existingBundle.properties || {};
      const existingBundleTitle = existingProperties._bundle_title || existingBundle.product_title || 'Bundle offer';
      const existingBundlePrice = existingProperties._bundle_price || moneyText(existingBundle.final_line_price, '');
      const existingBundleMrp = existingBundleItems.reduce((total, item) => total + Math.max(0, Number(item.original_line_price) || 0), 0);
      const existingBundlePriceText = String(existingBundlePrice).replace(/^₹\s*/, 'Rs. ');
      const existingBundleMrpText = existingBundleMrp ? Math.round(existingBundleMrp / 100).toLocaleString('en-IN') : '';
      const existingBundleImage = existingBundle.featured_image?.url || existingBundle.image || this.root.dataset.cartBundleImage || '';
      const imageMarkup = existingBundleImage
        ? `<img src="${escapeHtml(existingBundleImage)}" alt="">`
        : '<span class="dt-bundle-conflict__placeholder" aria-hidden="true"></span>';
      this.conflictBundleItems = existingBundleItems;
      this.lastFocus = this.addBundleButton;
      this.sheet.classList.remove('is-product-details');
      this.sheet.classList.add('is-bundle-conflict');
      this.sheetBody.innerHTML = `
        <section class="dt-bundle-conflict" aria-labelledby="DtBundleConflictTitle">
          <div class="dt-bundle-conflict__summary">
            <p id="DtBundleConflictTitle" class="dt-bundle-conflict__title">You already have a bundle in cart!</p>
            <div class="dt-bundle-conflict__rule"></div>
            <div class="dt-bundle-conflict__item">
              <div class="dt-bundle-conflict__image">${imageMarkup}</div>
              <p class="dt-bundle-conflict__name">${escapeHtml(existingBundleTitle)}</p>
              ${existingBundlePrice ? `<div class="dt-bundle-conflict__price-wrap"><p class="dt-bundle-conflict__price">${escapeHtml(existingBundlePriceText)}</p>${existingBundleMrpText ? `<p class="dt-bundle-conflict__mrp">MRP. ${escapeHtml(existingBundleMrpText)}</p>` : ''}</div>` : ''}
            </div>
          </div>
          <p class="dt-bundle-conflict__message">You can add only <strong>one bundle</strong> at a time.</p>
          <div class="dt-bundle-conflict__actions">
            <button type="button" class="dt-bundle-conflict__keep" data-bundle-conflict-keep>Cancel</button>
            <button type="button" class="dt-bundle-conflict__replace" data-bundle-conflict-replace>Replace bundle</button>
          </div>
        </section>
      `;
      this.openSheet();
    }

    async replaceExistingBundle() {
      const existingBundleItems = Array.isArray(this.conflictBundleItems) ? this.conflictBundleItems : [];
      if (!existingBundleItems.length) return;
      const replaceButton = this.sheetBody?.querySelector('[data-bundle-conflict-replace]');
      const keepButton = this.sheetBody?.querySelector('[data-bundle-conflict-keep]');
      if (replaceButton) {
        replaceButton.disabled = true;
        replaceButton.textContent = 'Replacing bundle...';
      }
      if (keepButton) keepButton.disabled = true;
      try {
        const updates = {};
        existingBundleItems.forEach((item) => { if (item.key) updates[item.key] = 0; });
        if (!Object.keys(updates).length) throw new Error('bundle_not_found');
        const removeResponse = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates })
        });
        if (!removeResponse.ok) throw new Error('bundle_remove_failed');
        this.closeSheet();
        await this.addBundleToCart({ skipExistingBundleCheck: true, replacedBundleItems: existingBundleItems });
      } catch (_) {
        if (replaceButton) {
          replaceButton.disabled = false;
          replaceButton.textContent = 'Replace bundle';
        }
        if (keepButton) keepButton.disabled = false;
        this.showError("We couldn't replace your bundle. Please try again.");
      }
    }

    openSheet() {
      if (!this.sheet) return;
      const stateVersion = ++this.sheetStateVersion;
      window.clearTimeout(this.sheetCloseTimer);
      if (this.sheetOpenFrame) window.cancelAnimationFrame(this.sheetOpenFrame);
      if (this.sheetOpenNestedFrame) window.cancelAnimationFrame(this.sheetOpenNestedFrame);
      this.sheetOpenFrame = null;
      this.sheetOpenNestedFrame = null;
      if (this.sheetPanel && this.sheetCloseHandler) this.sheetPanel.removeEventListener('transitionend', this.sheetCloseHandler);
      this.sheetCloseHandler = null;
      this.sheet.classList.remove('is-open', 'is-closing');
      this.sheet.hidden = false;
      document.body.classList.add(sheetOpenClass);
      this.sheetOpenFrame = window.requestAnimationFrame(() => {
        this.sheetOpenNestedFrame = window.requestAnimationFrame(() => {
          if (stateVersion !== this.sheetStateVersion || this.sheet.hidden) return;
          this.sheet.classList.add('is-open');
          if (this.sheetPanel) this.sheetPanel.focus();
        });
      });
    }

    closeSheet() {
      if (!this.sheet || this.sheet.hidden || this.sheet.classList.contains('is-closing')) return;
      ++this.sheetStateVersion;
      if (this.sheetOpenFrame) window.cancelAnimationFrame(this.sheetOpenFrame);
      if (this.sheetOpenNestedFrame) window.cancelAnimationFrame(this.sheetOpenNestedFrame);
      this.sheetOpenFrame = null;
      this.sheetOpenNestedFrame = null;
      const finishClose = () => {
        window.clearTimeout(this.sheetCloseTimer);
        if (this.sheetPanel && this.sheetCloseHandler) this.sheetPanel.removeEventListener('transitionend', this.sheetCloseHandler);
        this.sheetCloseHandler = null;
        this.sheet.hidden = true;
        this.sheet.classList.remove('is-closing', 'is-bundle-conflict');
        this.sheetBody.innerHTML = '';
      };
      const onTransitionEnd = (event) => {
        if (event.target === this.sheetPanel && event.propertyName === 'transform') {
          finishClose();
        }
      };
      this.sheetCloseHandler = onTransitionEnd;
      this.sheetPanel?.addEventListener('transitionend', this.sheetCloseHandler);
      this.sheet.classList.remove('is-open');
      this.sheet.classList.add('is-closing');
      document.body.classList.remove(sheetOpenClass);
      this.currentProduct = null;
      this.currentGroup = null;
      this.currentOptions = [];
      if (this.lastFocus && typeof this.lastFocus.focus === 'function') this.lastFocus.focus();
      this.sheetCloseTimer = window.setTimeout(finishClose, 380);
    }

    trapFocus(event) {
      const focusables = Array.from(this.sheet.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'));
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    showError(message) {
      if (this.error) this.error.textContent = message || '';
    }

    escape(value) {
      return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    }
  }

  function init() {
    document.querySelectorAll(rootSelector).forEach((root) => {
      if (root.dataset.bundleInitialized === 'true') return;
      root.dataset.bundleInitialized = 'true';
      new BundleBuilder(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
